import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	CurrencyAmountPipe,
	DollarShorthandPipe,
} from '@marmon/frontend/common';
import { MarginDistributionCalculation } from '@marmon/shared/types/analysis';
import {
	numberToPercentage,
	percentageToValue,
} from '@marmon/shared/types/common';
import { TuiHint } from '@taiga-ui/core';
import {
	AnalysisCardComponent,
	AnalysisCardType,
} from '../analysis-card/analysis-card.component';
import { DistributionBoxComponent } from '../box-distribution-card/distribution-box/distribution-box.component';
import {
	AnalysisColorCode,
	BoxDistribution,
	BoxDistributionCardBaseComponent,
	ColorCodeOptions,
} from './box-distribution-card-base.component';

@Component({
	standalone: true,
	imports: [
		AnalysisCardComponent,
		TuiHint,
		TranslocoPipe,
		CurrencyAmountPipe,
		DistributionBoxComponent,
		DollarShorthandPipe,
	],
	selector: 'mar-margin-box-distribution-card',
	templateUrl: 'box-distribution-card.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'contents' },
})
export class MarginBoxDistributionCardComponent extends BoxDistributionCardBaseComponent<MarginDistributionCalculation> {
	override readonly types = input<AnalysisCardType[]>(['percent']);

	override readonly targets = (data: MarginDistributionCalculation[]) => {
		if (this.hideTarget()) return null;

		const [head] = data;
		const value0 = numberToPercentage(
			head.marginInCents / head.revenueInCents,
		);
		return {
			0: value0,
			1: value0 + 0.07,
			2: value0 + 0.07,
			3: value0 + 0.15,
		};
	};

	override getDisplayData(): BoxDistribution[] {
		const data = this.inputData();

		const getData = (
			value: MarginDistributionCalculation,
			options: { code?: AnalysisColorCode; target?: number; box: number },
		) => {
			const { target, box } = options;
			const { marginInCents, revenueInCents } = value;

			const percent = marginInCents / revenueInCents;
			const code = target
				? (options.code ?? this.getColorCode({ percent, target, box }))
				: 'gray';

			return {
				code,
				id: crypto.randomUUID(),
				percent: percentageToValue(percent),
				value: value.marginInCents,
			};
		};

		const targets = this.targets ? this.targets(data) : null;
		const value: BoxDistribution[] = [
			{
				label: this.actualLabel,
				id: crypto.randomUUID(),
				data: data.map((d, index) => {
					if (index === 0) {
						return {
							value: d.marginInCents,
							code: 'gray',
							id: crypto.randomUUID(),
							percent: percentageToValue(
								d.marginInCents / d.revenueInCents,
							),
						};
					}

					const target = targets
						? targets[index as never]
						: undefined;
					return getData(d, { target, box: index });
				}),
			},
		];

		if (targets) {
			value.push({
				label: this.targetLabel,
				id: crypto.randomUUID(),
				data: data.map((d, index) => {
					const target = targets[index as never];
					return {
						code: 'gray',
						id: crypto.randomUUID(),
						value: d.marginInCents,
						percent: percentageToValue(target),
					};
				}),
			});
		}

		return value;
	}

	override getColorCode(options: ColorCodeOptions): AnalysisColorCode {
		const { percent, target, box } = options;
		if (target == null || percent == null || box == null) {
			console.error('Color could not be determined');
			return 'gray';
		}

		const delta = percent - target;
		let warningThreshold = -0.05; // default to last box value
		if (box === 0) return 'gray';
		if ([1, 2].includes(box)) {
			warningThreshold = -0.02;
		}

		if (delta >= 0) return 'green';
		if (delta >= warningThreshold) return 'yellow';
		return 'red';
	}
}
